import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { ReactComponent as UploadIcon } from "../../../../assets/Icon-Upload-White.svg";
import { DbUser } from "../../../../interfaces";
import { AlertState } from "../../../../types";
import { UserManagementModal } from "../UserManagementModal";
import axios from "../../../../services/axios";
import { buildCourseRequest } from "../utils/buildCourseRequest";
import {
  validateModules,
  validateQuestions,
  validateTargets,
  validateCourse,
} from "../utils/validateCourseRequest";
import { CoursePreviewModal } from "./CoursePreviewModal";

type ParsedCourseRequest = Awaited<ReturnType<typeof buildCourseRequest>>;

// Existing course used as a metadata template — admin picks one whose subject matches the new course (e.g. another Bio course for a new Bio course)
// so the shared metadata keys (icon, color, language_overrides, learning_experience, ltiClientId, lti_tool_client_id, top_app_bar_buttons, etc.) get inherited.
type TemplateCourse = {
  id: number;
  name: string;
  metadata: Record<string, unknown> | null;
};

export type AddCourseProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
};

export function AddCourse(props: AddCourseProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState<ParsedCourseRequest | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [existingCourses, setExistingCourses] = useState<TemplateCourse[]>([]);
  const [fetchingCourses, setFetchingCourses] = useState(false);
  const [templateCourse, setTemplateCourse] = useState<TemplateCourse | null>(null);

  // Fetch existing courses once when the modal opens — used as metadata templates.
  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    (async () => {
      try {
        setFetchingCourses(true);
        const res = await axios.get("/courses", {
          params: { disablePagination: true, bypassCache: true },
        });
        const list: TemplateCourse[] = (res.data?.results ?? res.data ?? []).map(
          (c: any) => ({
            id: c.id,
            name: c.name,
            metadata: c.metadata ?? null,
          })
        );
        if (!cancelled) setExistingCourses(list);
      } catch (e) {
        // non-fatal: the dropdown just stays empty and admin can still import
        console.warn("Failed to load existing courses for metadata template", e);
      } finally {
        if (!cancelled) setFetchingCourses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.open]);

  // if course_id is present in the sheet, we try to match it to an existing course.
  const sheetCourseId = useMemo<number | null>(() => {
    const id = (parsed?.course?.[0] as any)?.course_id;
    return typeof id === "number" && !Number.isNaN(id) ? id : null;
  }, [parsed]);

  const matchedCourse = useMemo<TemplateCourse | null>(() => {
    if (sheetCourseId == null) return null;
    return existingCourses.find((c) => Number(c.id) === sheetCourseId) ?? null;
  }, [sheetCourseId, existingCourses]);

  const effectiveTemplate = sheetCourseId != null ? matchedCourse : templateCourse;

  // sheet-driven keys (deeplink_asset_type, lti_grading_method,
  // lti_block_grade_passback, lti_grading_trigger)
  const mergedData = useMemo<ParsedCourseRequest | null>(() => {
    if (!parsed) return null;
    if (!effectiveTemplate?.metadata) return parsed;
    const course0: any = parsed.course[0] ?? {};
    return {
      ...parsed,
      course: [
        {
          ...course0,
          metadata: {
            ...(effectiveTemplate.metadata as Record<string, unknown>),
            ...((course0.metadata as Record<string, unknown>) ?? {}),
          },
        },
        ...parsed.course.slice(1),
      ],
    };
  }, [parsed, effectiveTemplate]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || !fileList[0]) return;
    const uploadedFile = fileList[0];
    event.target.value = "";

    if (!uploadedFile.name.endsWith(".xlsx")) {
      setError("Please upload a valid .xlsx file");
      return;
    }

    setFileName(uploadedFile.name);
    setError(null);
    setParsed(null);

    try {
      setParsing(true);
      const courseBuilderJSON = await buildCourseRequest(uploadedFile);
      const errors = [
        ...validateCourse(courseBuilderJSON.course),
        ...validateModules(courseBuilderJSON.modules),
        ...validateTargets(courseBuilderJSON.targets, courseBuilderJSON.modules),
        ...validateQuestions(courseBuilderJSON.questions, courseBuilderJSON.targets),
      ];
      if (errors.length) {
        throw new Error(errors.map((e) => e.message).join("\n"));
      }
      setParsed(courseBuilderJSON);
    } catch (err: any) {
      setError(err.message);
      setParsed(null);
    } finally {
      setParsing(false);
    }
  };

  const submit = async () => {
    if (!mergedData) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.post("/import/course", mergedData);
      props.onSubmit({
        severity: "success",
        message: response.data.message,
      });

      resetState();
      props.onClose();
    } catch (err: any) {
      setError(err.message);
      console.log("err", err);
      props.onSubmit({
        severity: "error",
        message: `Issues in course import`,
      });
      return false; //  prevents modal from closing
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setFileName(null);
    setError(null);
    setParsed(null);
    setTemplateCourse(null);
    setPreviewOpen(false);
  };

  const handleClose = () => {
    resetState();
    props.onClose();
  };

  return (
    <>
      <UserManagementModal
        title="Add course"
        open={props.open}
        onClose={handleClose}
        onSave={submit}
        disableSubmit={!mergedData || !effectiveTemplate || loading || parsing}>
        <Typography color="textSecondary" sx={{ marginBottom: "1rem" }}>
          Upload a Course Excel file.
        </Typography>

        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button
            component="label"
            variant="contained"
            size="large"
            sx={{ height: 40 }}
            disableElevation
            disabled={parsing}>
            <UploadIcon width={16} />
            Choose File
            <input hidden type="file" accept=".xlsx" onChange={handleFileChange} />
          </Button>

          {parsed && !parsing && (
            <Button
              variant="outlined"
              size="large"
              sx={{ height: 40 }}
              startIcon={<VisibilityIcon />}
              onClick={() => setPreviewOpen(true)}>
              See Preview
            </Button>
          )}
        </Stack>

        <Typography variant="body2" sx={{ marginTop: 1 }}>
          {fileName ?? "No file chosen"}
        </Typography>

        {/* When the sheet has a course_id, its own course metadata is inherited
            automatically — no manual picker. Sheet-derived keys still win on conflict. */}
        {sheetCourseId != null ? (
          <Box sx={{ mt: 3 }}>
            {fetchingCourses && !matchedCourse ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Looking up course #{sheetCourseId}…
                </Typography>
              </Stack>
            ) : matchedCourse ? (
              <Typography variant="body2" color="text.secondary">
                Inheriting metadata from existing course #{sheetCourseId} (
                <strong>{matchedCourse.name}</strong>) - its metadata (icon, color,
                language_overrides, LTI client IDs, learning_experience, etc.) is copied
                and merged with the sheet-derived fields.
              </Typography>
            ) : (
              <Typography color="error" variant="body2">
                Sheet references course_id {sheetCourseId}, but no existing course with
                that ID was found. Remove the course_id to import as a new course, or fix
                it in the sheet.
              </Typography>
            )}
          </Box>
        ) : (
          /* Metadata template picker — required. Sheet-derived keys still win on conflict. */
          <TextField
            sx={{ mt: 3 }}
            label="Inherit metadata from existing course"
            required
            value={templateCourse?.id ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              const found =
                existingCourses.find((c) => String(c.id) === String(raw)) ?? null;
              setTemplateCourse(found);
            }}
            disabled={fetchingCourses && existingCourses.length === 0}
            helperText="Pick a similar course (same subject) — its metadata (icon, color, language_overrides, LTI client IDs, learning_experience, etc.) will be copied and merged with the sheet-derived fields."
            fullWidth
            select>
            {fetchingCourses && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {existingCourses.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
            {!fetchingCourses && existingCourses.length < 1 && (
              <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                No existing courses found
              </Typography>
            )}
          </TextField>
        )}

        {parsing && (
          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Parsing and validating…
            </Typography>
          </Stack>
        )}

        {parsed && !parsing && !error && (
          <Typography
            variant="body2"
            color={effectiveTemplate ? "success.main" : "text.secondary"}
            sx={{ mt: 2, fontWeight: 500 }}>
            {effectiveTemplate
              ? `✓ Validation passed — ready to import (inheriting metadata from "${effectiveTemplate.name}").`
              : sheetCourseId != null
              ? "✓ Validation passed — resolving the course referenced by course_id to enable Save."
              : "✓ Validation passed — pick a course above to inherit metadata from to enable Save."}
          </Typography>
        )}

        {error && (
          <Typography color="error" sx={{ marginTop: 2, whiteSpace: "pre-line" }}>
            {error}
          </Typography>
        )}
      </UserManagementModal>

      <CoursePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={mergedData}
      />
    </>
  );
}
